// Request util for api call
import request from '~/utils/request';

// Actions Types
import {
  SET_ADMIN_ORDERS_DATA,
  SET_ADMIN_ORDERS_FILTERS_LABELS,
  SET_ADMIN_ORDERS_LOADER,
  SET_ADMIN_ORDER_DISPATCH_LOADER,
} from './action-types';

// Utiils
import { dateFormat } from '~/utils/date-format';
import { DEFAULT_PAGINATION } from '~/utils/constants';
import { balanceFormatter, formatCount } from '~/utils/helperFunctions';

const isFindNewContactsPurchaseOrder = (order) =>
  Boolean(
    order?.meta?.source === 'find_new_contacts_data_axle' || order?.meta?.findNewContactsPurchase
  );

const getFindNewContactsProductTooltip = (order) => {
  const meta = order?.meta || {};
  const purchase = meta.purchase || {};
  const targetCount = purchase.quantity || meta.targetCount || order.quantity;
  const listName = meta.listName || purchase.listName || order.importListName || order.name;
  const recordLabel = `${formatCount(targetCount)} record${Number(targetCount) === 1 ? '' : 's'}`;

  return listName ? `${listName}, ${recordLabel}` : recordLabel;
};

const getProductTooltip = (order) => {
  if (isFindNewContactsPurchaseOrder(order)) return getFindNewContactsProductTooltip(order);

  return `${order?.product?.paperSize || '-'} ${order?.product?.productType || 'Product'}, ${order?.product?.deliveryType || '-'}`;
};

const getFindNewContactsQuantity = (order) => {
  const meta = order?.meta || {};
  const purchase = meta.purchase || {};

  return purchase.quantity || meta.targetCount || order.quantity;
};

const fetchAdminOrders = (payload) => async (dispatch) => {
  try {
    dispatch({ type: SET_ADMIN_ORDERS_LOADER, payload: true });

    let url = 'admin/orders';

    let queryPayload = {
      page: payload?.pagination?.page ? payload.pagination.page : DEFAULT_PAGINATION.page,
      pageSize: payload?.pagination?.pageSize
        ? payload.pagination.pageSize
        : DEFAULT_PAGINATION.pageSize,
    };

    if (payload?.search?.length) {
      queryPayload.search = payload.search;
    }

    if (payload?.applyFilters) {
      Object.entries(payload.filters).forEach(([key, value]) => {
        if (key === 'createdAt' || key === 'scheduledDate' || key === 'mailedDate') {
          // If the value is an object, iterate over its properties
          if (value) {
            let date = Object?.entries(value)
              .map(
                ([nestedKey, nestedValue]) =>
                  `${JSON.stringify(nestedKey)}:${JSON.stringify(nestedValue)}`
              )
              .join(', ');
            queryPayload[key] = `{${date}}`;
          }
        } else if (value && value.length) {
          queryPayload[key] = `${JSON.stringify(value)}`;
        }
      });
    }

    if (payload?.batchId) {
      queryPayload.batchId = payload.batchId;
    }

    const response = await request.get(url, queryPayload);

    if (response.status === 200) {
      const combinedData = response.data.data.rows.map((order, i) => ({
        key: i,
        orderId: order.id,
        userId: order.createdBy,
        createdBy: order?.creator?.fullName || ' -',
        batchId: order.batchId,
        source: order.source,
        productType: isFindNewContactsPurchaseOrder(order)
          ? 'Find New Contacts'
          : order.product?.productType || ' -',
        quantity: isFindNewContactsPurchaseOrder(order) ? getFindNewContactsQuantity(order) : order.quantity,
        deliverableQuantity: isFindNewContactsPurchaseOrder(order)
          ? getFindNewContactsQuantity(order)
          : order.deliverableQuantity,
        revenue: order.cost ? balanceFormatter(order.cost) : ' -',
        scheduleDate: dateFormat(order?.deliveryDate, true, true),
        scheduledMailingDate: order?.mailingDate,
        mailingDate: dateFormat(order?.mailedDate, true, true),
        status: order.status,
        paymentStatus: order.paymentStatus,
        dispatchedAt: order.dispatchedAt,
        checked: false,
        productId: order.productId,
        product: order.product,
        productTooltip: getProductTooltip(order),
        payloadBackupURL: order.payloadBackupURL,
      }));

      const orders = {
        rows: combinedData,
        count: response.data.data.count,
        totalRecordsInDB: response.data.data.totalRecordsInDB,
        to: response.data.data.to,
        from: response.data.data.from,
        currentPage: response.data.data.currentPage,
        lastPage: response.data.data.lastPage,
        perPage: response.data.data.perPage,
        totalRevenue: response.data.data.totalRevenue,
        totalOrders: response.data.data.total,
      };

      dispatch({ type: SET_ADMIN_ORDERS_DATA, payload: orders });
    }
  } catch (error) {
    return error;
  } finally {
    setTimeout(() => {
      dispatch({ type: SET_ADMIN_ORDERS_LOADER, payload: false });
    }, 10);
  }
};

const fetchAdminOrdersFilters = () => async (dispatch) => {
  try {
    const filters = await request.get('admin/orders/filters-data');
    let modifiedLabels = {};
    Object.keys(filters.data.data).forEach((key) => {
      if (typeof filters.data.data[key] === 'object') {
        const combinedData = Object.entries(filters.data.data[key]).map(([valueKey, item], id) => ({
          id,
          title: item,
          value: valueKey || item,
        }));
        modifiedLabels[key] = combinedData;
      }
    });
    dispatch({ type: SET_ADMIN_ORDERS_FILTERS_LABELS, payload: modifiedLabels });
  } catch (error) {
    return error;
  }
};

const cancelOrderByAdmin = (payload) => async () => {
  try {
    const cancelOrder = await request.delete(`admin/orders/${payload}`);
    return cancelOrder;
  } catch (error) {
    return error.response;
  }
};

const dispatchOrderNowByAdmin = (payload) => async (dispatch) => {
  try {
    dispatch({ type: SET_ADMIN_ORDER_DISPATCH_LOADER, payload: payload.orderId });

    const response = await request.post('admin/orders/dispatch-now', payload);

    return response;
  } catch (error) {
    return error.response;
  } finally {
    dispatch({ type: SET_ADMIN_ORDER_DISPATCH_LOADER, payload: null });
  }
};

// Parse "M/DD/YYYY" as UTC midnight to avoid timezone shift across environments
const parseDateStringAsUTC = (dateStr) => {
  const parts = dateStr.split('/');
  return new Date(Date.UTC(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1])));
};

const rescheduleOrderByAdmin = (payload) => async (dispatch) => {
  try {
    dispatch({ type: SET_ADMIN_ORDER_DISPATCH_LOADER, payload: payload.orderId });
    const { orderId, mailingDate, isAdmin } = payload;
    const endpoint = isAdmin ? 'admin/orders/reschedule/' : 'orders/reschedule/';

    const mailingDateISO = parseDateStringAsUTC(mailingDate).toISOString();

    const response = await request.post(endpoint + orderId, { mailingDate: mailingDateISO });
    return response;
  } catch (error) {
    return error.response;
  } finally {
    dispatch({ type: SET_ADMIN_ORDER_DISPATCH_LOADER, payload: null });
  }
};

const adminOrdersBulkActions = async (payload) => {
  try {
    let url = '/admin/orders/export';

    const queryPayload = {};

    queryPayload.ids = JSON.stringify(payload.ids);
    queryPayload.action = payload.action;

    if (payload?.search?.length) {
      queryPayload.search = payload.search;
    }

    if (payload?.applyFilters) {
      Object.entries(payload.filters).forEach(([key, value]) => {
        if (key === 'createdAt' || key === 'scheduledDate' || key === 'mailedDate') {
          // If the value is an object, iterate over its properties
          if (value) {
            let date = Object?.entries(value)
              .map(
                ([nestedKey, nestedValue]) =>
                  `${JSON.stringify(nestedKey)}:${JSON.stringify(nestedValue)}`
              )
              .join(', ');
            queryPayload[key] = `{${date}}`;
          }
        } else if (value && value.length) {
          queryPayload[key] = `${JSON.stringify(value)}`;
        }
      });
    }

    if (payload?.batchId) {
      queryPayload.batchId = payload.batchId;
    }

    const orderBulkExport = await request.get(url, queryPayload);
    return orderBulkExport;
  } catch (error) {
    return error.response;
  }
};

export {
  fetchAdminOrders,
  fetchAdminOrdersFilters,
  cancelOrderByAdmin,
  dispatchOrderNowByAdmin,
  rescheduleOrderByAdmin,
  adminOrdersBulkActions,
};
